import type { AxiosResponse } from 'axios';
import type {
  LoginResponse,
  MfaEnrollVerifyResponse,
  MfaRemoveRequest,
  MfaRemoveResponse,
  MfaStatusResponse,
  MfaDispatchResponse,
  MfaVerifyRequest,
  MfaVerifySuccess,
  SmsEmailEnrollStartResponse,
  TotpEnrollStartResponse,
} from '../../types/authMfa';
import type { Device } from '../../types/device';
import type { Group, User } from '../../types/user';
import API from './axios';

interface Credentials {
  email: string;
  password: string;
}

interface FetchUsersParams {
  page?: number;
  limit?: number;
  sortBy?: {
    field: string;
    sort: 'ASC' | 'DESC';
  };
  filterBy?: {
    OrganizationID?: number;
    Name?: string;
    AccessLevel?: number;
    GroupID?: number;
  };
}

// FetchGroupsParams interface
interface FetchGroupsParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  filterBy?: { [key: string]: string | undefined };
}

export const getTimeZonesApiCall = async (): Promise<AxiosResponse<string[]>> => {
  return API.get<string[]>('api/admin/get-timezone');
};

export const getMessageTemplateApiCall = async (): Promise<AxiosResponse<any>> => {
  return API.get('api/admin/get-message-template');
};

export const updateMessageTemplateApiCall = async (
  message: string
): Promise<AxiosResponse<any>> => {
  return API.patch('api/admin/update-message-template', { message });
};

import { getToken } from '../../utils/security/tokenStorage';

export const validateJwtApiCall = async (): Promise<AxiosResponse<any>> => {
  return API.get('api/auth/validate-jwt', {
    headers: {
      // Query token from secure storage to prevent using cached value
      Authorization: `Bearer ${getToken()}`,
    },
  });
};

export const loginApiCall = async (
  credentials: Credentials
): Promise<AxiosResponse<LoginResponse>> => {
  return API.post<LoginResponse>('api/auth/login', credentials);
};

export const mfaVerifyApiCall = async (
  body: MfaVerifyRequest
): Promise<AxiosResponse<MfaVerifySuccess>> => {
  return API.post<MfaVerifySuccess>('api/auth/mfa/verify', body);
};

export const mfaDispatchApiCall = async (body: {
  mfaContextToken: string;
  channel: 'sms' | 'email';
}): Promise<AxiosResponse<MfaDispatchResponse>> => {
  return API.post<MfaDispatchResponse>('api/auth/mfa/dispatch', body);
};

/** Logged-in MFA refresh for step-up routes (`mfa_at` stale). Requires full session JWT. */
export const mfaStepUpStartApiCall = async (): Promise<AxiosResponse<LoginResponse>> => {
  return API.post<LoginResponse>('api/auth/mfa/step-up/start', {});
};

export const mfaSmsEnrollStartApiCall = async (
  body?: { phone?: string }
): Promise<AxiosResponse<SmsEmailEnrollStartResponse>> => {
  return API.post<SmsEmailEnrollStartResponse>('api/auth/mfa/sms/enroll/start', body ?? {});
};

export const mfaSmsEnrollVerifyApiCall = async (body: {
  enrollToken: string;
  code: string;
}): Promise<AxiosResponse<MfaEnrollVerifyResponse>> => {
  return API.post<MfaEnrollVerifyResponse>('api/auth/mfa/sms/enroll/verify', body);
};

export const mfaEmailEnrollStartApiCall = async (
  body?: { email?: string }
): Promise<AxiosResponse<SmsEmailEnrollStartResponse>> => {
  return API.post<SmsEmailEnrollStartResponse>('api/auth/mfa/email/enroll/start', body ?? {});
};

export const mfaEmailEnrollVerifyApiCall = async (body: {
  enrollToken: string;
  code: string;
}): Promise<AxiosResponse<MfaEnrollVerifyResponse>> => {
  return API.post<MfaEnrollVerifyResponse>('api/auth/mfa/email/enroll/verify', body);
};

export const mfaTotpEnrollStartApiCall = async (
  body?: { friendlyName?: string }
): Promise<AxiosResponse<TotpEnrollStartResponse>> => {
  return API.post<TotpEnrollStartResponse>('api/auth/mfa/totp/enroll/start', body ?? {});
};

export const mfaTotpEnrollVerifyApiCall = async (body: {
  enrollToken: string;
  code: string;
}): Promise<AxiosResponse<MfaEnrollVerifyResponse>> => {
  return API.post<MfaEnrollVerifyResponse>('api/auth/mfa/totp/enroll/verify', body);
};

export const mfaRemoveApiCall = async (
  body: MfaRemoveRequest
): Promise<AxiosResponse<MfaRemoveResponse>> => {
  return API.post<MfaRemoveResponse>('api/auth/mfa/remove', body);
};

export const mfaStatusApiCall = async (): Promise<AxiosResponse<MfaStatusResponse>> => {
  return API.get<MfaStatusResponse>('api/auth/mfa/status');
};

export const logoutApiCall = async (): Promise<AxiosResponse<any>> => {
  return API.post('api/auth/logout', {});
};

export const changePasswordApiCall = async (credentials: any): Promise<AxiosResponse<any>> => {
  return API.post('api/auth/change-password', credentials);
};

export const forgotPasswordApiCall = async (email: string): Promise<AxiosResponse<any>> => {
  return API.post('api/auth/forgot-password', { email });
};

export const resetPasswordApiCall = async (
  token: string,
  credentials: any
): Promise<AxiosResponse<any>> => {
  return API.post(`api/auth/reset-password/${token}`, credentials);
};

export const getUsersApiCall = async (
  params: FetchUsersParams = {}
): Promise<AxiosResponse<any>> => {
  return API.get('api/admin/users', {
    params,
  });
};

/**
 * @param {Object} [params]
 * @param {number} [params.page]
 * @param {number} [params.limit]
 * @param {string} [params.sortBy]
 * @param {Object} [params.filterBy]
 */
export const getGroupsApiCall = async (
  params: FetchGroupsParams = {}
): Promise<AxiosResponse<any>> => {
  return API.get('api/admin/groups', {
    params,
  });
};

export const getGroupsBasicApiCall = async (
  params: FetchGroupsParams = {}
): Promise<AxiosResponse<any>> => {
  return API.get('api/admin/groups/basic', {
    params,
  });
};

/**
 * Count of ungrouped devices visible to the signed-in user. No query params.
 * Only super admin, organization admin, and organization viewer — others get 403.
 */
export const getUnassignedDevicesCountApiCall = async (): Promise<AxiosResponse<number>> => {
  return API.get('api/admin/groups/unassigned-count');
};

export const getOrganizationsApiCall = async (): Promise<AxiosResponse<any>> => {
  return API.get('api/admin/organization');
};

export const getAdminDevicesApiCall = async (): Promise<AxiosResponse<any>> => {
  return API.get('api/admin/devices');
};

export const getTransitionalDevicesApiCall = async (): Promise<AxiosResponse<any>> => {
  return API.get('api/admin/transitional-devices');
};

export const getTableColumnsApiCall = async (): Promise<AxiosResponse<any>> => {
  return API.get('api/device-records/get-table-columns');
};

export const addUserApiCall = async (user: User): Promise<AxiosResponse<any>> => {
  return API.post('api/admin/user', user);
};

export const addBulkUsers = async (users: User[] | any): Promise<AxiosResponse<any>> => {
  return API.post('api/admin/bulk-users', users);
};

export const editUserApiCall = async (
  user: Partial<User>,
  UserID: string
): Promise<AxiosResponse<any>> => {
  return API.patch('api/admin/user', user, {
    params: {
      UserID,
    },
  });
};

export const deleteUserApiCall = async (UserID: string): Promise<AxiosResponse<any>> => {
  return API.delete('api/admin/user', {
    params: {
      UserID,
    },
  });
};

export const addGroupApiCall = async (data: Group): Promise<AxiosResponse<any>> => {
  return API.post('api/admin/groups', data);
};

export const editGroupApiCall = async (
  data: Partial<Group>,
  GroupID: string
): Promise<AxiosResponse<any>> => {
  return API.put(`api/admin/groups/${GroupID}`, data);
};

export const deleteGroupApiCall = async (GroupID: string): Promise<AxiosResponse<any>> => {
  return API.delete(`api/admin/groups/${GroupID}`);
};

export const addDeviceApiCall = async (device: Device): Promise<AxiosResponse<any>> => {
  return API.post('api/admin/device', device);
};

export const editDeviceApiCall = async (
  device: Partial<Device>,
  DeviceID: string
): Promise<AxiosResponse<any>> => {
  return API.patch('api/admin/device', device, {
    params: {
      DeviceID,
    },
  });
};

export const transferDevicesApiCall = async (data: any): Promise<AxiosResponse<any>> => {
  return API.post('api/admin/transfer-devices', data);
};

export const deleteDeviceApiCall = async (DeviceID: string): Promise<AxiosResponse<any>> => {
  return API.delete('api/admin/device', {
    params: {
      DeviceID,
    },
  });
};

export const searchTransitionalDeviceApiCall = async (
  serialNumber: string
): Promise<AxiosResponse<any>> => {
  return API.get(`api/admin/search-transitional-device/${serialNumber}`);
};

export const transferTransitionalDeviceApiCall = async ({
  SerialNumber,
  GroupIDs,
  OrganizationID,
}: {
  SerialNumber: string;
  GroupIDs: string[];
  OrganizationID?: string | number;
}): Promise<AxiosResponse<any>> => {
  return API.post('api/admin/transfer-transitional-device', {
    SerialNumber,
    GroupIDs,
    ...(OrganizationID != null && OrganizationID !== '' ? { OrganizationID } : {}),
  });
};

export const bulkTransferTransitionalDevicesApiCall = async (
  devices: any
): Promise<AxiosResponse<any>> => {
  return API.post('api/admin/bulk-transfer-transitional-devices', { devices });
};
